import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { UsageLogsApi } from '@/services/usage-logs.api';
import { PageHeader } from '@/components/ui/PageHeader';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

/** Per-user drill-down for Usage History — which endpoints this one user has (and hasn't) hit. */
export function UserUsageHistoryPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());

  const { data, isLoading } = useQuery({
    queryKey: ['usage-logs-user-summary', userId, { dateFrom, dateTo }],
    queryFn: () => UsageLogsApi.userSummary(userId, { dateFrom, dateTo }),
    enabled: !!userId,
  });

  return (
    <>
      <Link to="/usage-history" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Usage History
      </Link>

      <PageHeader
        title={data ? data.user.name : 'Usage History'}
        description={data?.user.email ?? undefined}
      />

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-slate-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="px-4 py-6 text-sm text-slate-400">Loading…</div>
      ) : !data ? (
        <div className="px-4 py-6 text-sm text-slate-400">No data.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Used Endpoints — {data.usedEndpoints.length}
            </div>
            {data.usedEndpoints.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-400">No hits in this range.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.perEndpoint.map((e) => (
                  <div key={`${e.method} ${e.routePattern}`} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <div className="min-w-0">
                      <div className="truncate font-mono text-xs font-medium text-slate-900">
                        <span className="text-brand-600">{e.method}</span> {e.routePattern}
                      </div>
                      <div className="text-xs text-slate-500">
                        avg {e.avgDurationMs}ms · last {fmtDateTime(e.lastHitAt)}
                      </div>
                    </div>
                    <div className="font-semibold text-slate-900">{e.hits}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Never Used by This User — {data.neverUsedEndpoints.length}
            </div>
            {data.neverUsedEndpoints.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-400">Every known endpoint was used at least once.</div>
            ) : (
              <div className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
                {data.neverUsedEndpoints.map((r) => (
                  <div key={`${r.method} ${r.pattern}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="font-mono text-xs text-brand-600">{r.method}</span>
                    <span className="font-mono text-xs text-slate-900">{r.pattern}</span>
                    <span className="text-xs text-slate-500">— {r.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
